import { useEffect, useRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useBooking } from "@/context/BookingContext";
import { bodyAreas, getArea, getTreatment, ui } from "@/content";
import BookButtonInline from "./PanelBookButton";

export default function AreaPanel({ slug, open, onOpenChange, onSelect }) {
  const desktop = useMediaQuery("(min-width: 768px)");
  const { openBooking } = useBooking();
  const activeAreaRef = useRef(null);
  const area = getArea(slug);

  useEffect(() => {
    if (!open || !activeAreaRef.current) return;
    activeAreaRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [open, slug]);
  if (!area) return null;

  const book = () => {
    onOpenChange(false);
    setTimeout(openBooking, 180);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="np-overlay fixed inset-0 z-50 bg-forest/40 backdrop-blur-[2px]" />
        <Dialog.Content
          data-testid="area-panel"
          data-variant={desktop ? "drawer" : "sheet"}
          className={
            desktop
              ? "np-drawer fixed inset-y-0 right-0 z-50 flex w-full max-w-[460px] flex-col bg-cream shadow-2xl"
              : "np-sheet fixed inset-x-0 bottom-0 z-50 flex max-h-[88vh] flex-col rounded-t-[28px] bg-cream shadow-2xl"
          }
        >
          {!desktop && <div className="mx-auto mt-3 h-1.5 w-12 shrink-0 rounded-full bg-ink/15" aria-hidden="true" />}
          <div className="flex items-start justify-between gap-4 px-6 pb-4 pt-5 sm:px-8 sm:pt-8">
            <div className="min-w-0">
              <p className="eyebrow">{ui.selectedArea}</p>
              <Dialog.Title className="mt-1 font-serif text-4xl text-ink" data-testid="area-panel-title">{area.label}</Dialog.Title>
            </div>
            <Dialog.Close aria-label={ui.close} data-testid="area-panel-close" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-ink ring-1 ring-line hover:bg-sand">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          <div className="px-6 sm:px-8">
            <p className="sr-only">{ui.switchArea}</p>
            <div className="no-scrollbar -mx-6 flex gap-2 overflow-x-auto px-6 pb-3 sm:-mx-8 sm:px-8" role="group" aria-label={ui.switchArea}>
              {bodyAreas.map((a) => (
                <button ref={a.slug === slug ? activeAreaRef : null} key={a.slug} type="button" onClick={() => onSelect(a.slug)} aria-pressed={a.slug === slug} data-testid={`panel-switch-${a.slug}`} className={`chip chip-sm shrink-0 ${a.slug === slug ? "is-active" : ""}`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2 sm:px-8">
            <Dialog.Description className="text-base leading-relaxed text-muted">{area.lead}</Dialog.Description>
            <h3 className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-muted">{ui.areaConditions}</h3>
            <ul className="mt-3 grid gap-2" data-testid="area-panel-conditions">
              {area.conditions.map((c) => (
                <li key={c} className="flex gap-2.5 text-[15px] text-ink">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-pine" aria-hidden="true" /> {c}
                </li>
              ))}
            </ul>
            <h3 className="mt-7 text-xs font-semibold uppercase tracking-[0.16em] text-muted">{ui.areaTreatments}</h3>
            <ul className="mt-3 grid gap-2" data-testid="area-panel-treatments">
              {area.treatments.map((id) => {
                const t = getTreatment(id);
                return (
                  <li key={id}>
                    <Link to={`/behandlingar#${id}`} onClick={() => onOpenChange(false)} data-testid={`area-panel-treatment-${id}`} className="group flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-line transition-[box-shadow] hover:shadow-md">
                      <span className="font-medium text-ink">{t.name}</span>
                      <ArrowRight className="h-4 w-4 text-pine transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-sm text-muted">{ui.areaDisclaimer}</p>
          </div>
          <div className="flex flex-col gap-3 border-t border-line bg-cream px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:px-8">
            <BookButtonInline onClick={book} />
            <Link to={`/besvar#${area.slug}`} onClick={() => onOpenChange(false)} data-testid="area-panel-read-more" className="inline-flex h-12 items-center justify-center gap-2 rounded-full px-5 text-[15px] font-semibold text-forest ring-1 ring-forest/25 hover:bg-sand">
              {ui.readMoreArea}
            </Link>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
